import { CJEvent } from 'types/event';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import { addToCJPastAtom } from 'containers/CahierJournal/queries/historyAtoms';
import {
  deserializeEvent,
  serializeCJSeanceUpdateView,
  cleanPendingEphemeralGroups,
} from 'containers/CahierJournal/queries/serializer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useAtom } from 'jotai';
import { CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY } from 'containers/CahierJournal/queries/useGetDaysWithEvents';
import { EPHEMERAL_GROUPS_KEY } from 'queries/eleveGroups/useEphemeralEleveGroups';

interface UpdateEventPayload {
  event: CJEvent;
  previousDay?: Date;
}

export function useUpdateCJSeance(disableUndoRedo?: boolean) {
  const { schoolyear, school } = useSchoolSchoolyear();
  const queryClient = useQueryClient();
  const user = useUser();

  const [, addToPast] = useAtom(addToCJPastAtom);
  const { mutateAsync } = useMutation<CJEvent, any, UpdateEventPayload>({
    mutationFn: ({ event }) => {
      const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/cahier-journal/seances/${event.id}`;
      return request(requestURL, {
        method: 'PATCH',
        body: serializeCJSeanceUpdateView(event, user.location),
      }).then(v => deserializeEvent(v, user.location));
    },
    retry: (failCount, err) => {
      if (err?.response?.status === 403) {
        return false;
      }
      return failCount < 5;
    },
    onMutate: ({ event, previousDay }) => {
      // Set isDemo to false when updating an event and clean pending ephemeral groups
      const updatedEvent = cleanPendingEphemeralGroups({
        ...event,
        isDemo: false,
      });

      if (!disableUndoRedo) {
        const dayPreviousEvent = previousDay || event.start;
        const previousDayData = queryClient.getQueryData<CJEvent[]>([
          schoolyear.id,
          school.id,
          EVENTS_KEY,
          format(dayPreviousEvent, 'fr-FR'),
        ]);
        const previousEvent = (previousDayData || []).find(
          evt => evt.id === event.id,
        );
        addToPast({
          payload: previousEvent
            ? [previousEvent, updatedEvent]
            : [updatedEvent],
          action: 'UPDATE',
        });
      }

      if (
        previousDay &&
        format(event.start, 'fr-FR') !== format(previousDay, 'fr-FR')
      ) {
        queryClient.setQueryData<CJEvent[]>(
          [schoolyear.id, school.id, EVENTS_KEY, format(previousDay, 'fr-FR')],
          old => (old || []).filter(e => e.id !== event.id),
        );
        queryClient.setQueryData<CJEvent[]>(
          [schoolyear.id, school.id, EVENTS_KEY, format(event.start, 'fr-FR')],
          old => [...(old || []), updatedEvent],
        );
      } else {
        queryClient.setQueryData<CJEvent[]>(
          [schoolyear.id, school.id, EVENTS_KEY, format(event.start, 'fr-FR')],
          old =>
            (old || []).map(ev => (ev.id === event.id ? updatedEvent : ev)),
        );
      }
    },
    onSuccess: ({}, { previousDay, event }) => {
      if (
        previousDay &&
        format(event.start, 'fr-FR') !== format(previousDay, 'fr-FR')
      ) {
        queryClient.invalidateQueries({
          queryKey: [
            schoolyear.id,
            school.id,
            CAHIER_JOURNAL_DAYS_WITH_EVENTS_KEY,
          ],
        });
      }

      queryClient.invalidateQueries({
        queryKey: [schoolyear.id, school.id, EPHEMERAL_GROUPS_KEY],
      });
    },
  });
  return mutateAsync;
}
